import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { getFile } from "../api/documentUpload";
import {
  getByValueAndKey,
  getDropdownData,
  getTextByValueAndKey,
} from "../api/keyvalue";
import FileUploader from "./FileUploader";
import Grid from "./Grid";
import LoadingSpinner from "./LoadingSpinner";
import Button from "./input/Button";
import Dropdown from "./input/Dropdown";
import Input from "./input/Input";
import ButtonRenderer from "./renderer/ButtonRenderer";
import { documentValidation } from "./validations/stakeholderCreation";
import { AUTH_TYPES, FORMS, MODULES } from "../const";
import FileCellRenderer from "./renderer/FileCellRenderer";
import { isNumber } from "../helper";
import { hasMultiple } from "../common/logic";
import { toast } from "react-toastify";
import DeleteButtonRenderer from "./renderer/DeleteButtonRenderer";

const FORM = FORMS.COMMON.DOCUMENTS.form;

// ---- helpers for matching file -> extension option ----
const _clean = (v) => (v == null ? "" : String(v).trim());
const _lower = (v) => _clean(v).toLowerCase();
const _normalizeExt = (v) => {
  const s = _lower(v);
  if (!s) return null;
  const m = s.match(/(\.[a-z0-9]+)$/i);
  if (m) return m[1].toLowerCase();
  const parts = s.split(/[^a-z0-9.]+/).filter(Boolean);
  const last = parts[parts.length - 1];
  return last ? (last.startsWith(".") ? last : `.${last}`) : null;
};

// Extract ALL possible extensions from a string like "JPG / JPEG" or "PDF"
const _normalizeExtList = (v) => {
  const s = _lower(v);
  if (!s) return [];
  const tokens = s
    .split(/[\\/|]|(?:\s+(?:or|and)\s+)/)
    .map((t) => t.trim())
    .filter(Boolean);
  const exts = [];
  for (const token of tokens) {
    const normalized = _normalizeExt(token);
    if (normalized) exts.push(normalized);
  }
  return exts;
};

const DocumentUpload = forwardRef(
  (
    {
      opeType,
      dropdown,
      isLoading,
      currentValues,
      editMode,
      reference,
      viewOnlyMode = false,
      module = MODULES.Master,
      isOnTheModal = false,
      showGridOnly = false,
    },
    ref,
  ) => {
    const [files, setFiles] = useState([]);
    const [docCount, setDocCount] = useState(2);
    const [documents, setDocuments] = useState([]);
    const [currentErrors, setCurrentErrors] = useState({});
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [currentDocument, setCurrentDocument] = useState({});
    const [buttonClicked, setButtonClicked] = useState(false);
    const [docCatSelected, setDocCatSelected] = useState(false);
    const [extensionOptions, setExtensionOptions] = useState([]);
    const [isExtOptLoading, setIsExtOptLoading] = useState(false);
    const [isExtensionLoading, setIsExtensionLoading] = useState(false);
    const [currentFileExtension, setCurrentFileExtension] = useState(null);
    const [isEditModeDataLoading, setIsEditModeDataLoading] = useState(false);
    const [deletedList, setDeletedList] = useState([]);
    const user = useSelector((state) => state.user);

    const [documentData, setDocumentData] = useState({
      docOperation: opeType,
      docCategory: null,
      docReference: reference,
      fileType: null, // will store keyId/value/key from extensionOptions
      fileName: null,
      docName: "",
      docUploadStatus: 1,
      docUploadStatusValue: "",
      docStatus: opeType === 8 ? 1 : 2,
      docActive: 1,
      docCategoryValue: "",
      fileTypeValue: "",
      fileNameValue: "",
      icon: "",
      author: user.username,
      persisted: false,
      isRowLoading: false,
      fileLoadError: false,
    });

    useEffect(() => {
      if (documentData.docCategory) {
        (async () => {
          setIsExtOptLoading(true);
          const extensionData = await getDropdownData(
            "file_type",
            documentData.docCategory.toString(),
          );
          setExtensionOptions(extensionData);
          setIsExtOptLoading(false);
        })();
      } else {
        setExtensionOptions([]);
      }
    }, [documentData.docCategory]);

    // Auto-select extension when a file is picked/dropped
    const handleFileSelected = (uploadedFile) => {
      if (!uploadedFile) return;

      const fileMime = _lower(uploadedFile.type); // e.g., "application/pdf"
      const name = _clean(uploadedFile.name);
      const fileExt = (() => {
        const idx = name.lastIndexOf(".");
        return idx === -1 ? null : name.substring(idx).toLowerCase(); // ".pdf"
      })();

      const match = (extensionOptions || []).find((opt) => {
        const optMime = _lower(opt?.keyValue2 ?? opt?.mime ?? opt?.contentType);

        // Get all possible extensions from the option (handles "JPG / JPEG" etc.)
        const candidateFields = [
          opt?.keyValue1,
          opt?.keyDescription,
          opt?.text,
          opt?.label,
        ];
        const optExts = [];
        candidateFields.forEach((field) => {
          const extList = _normalizeExtList(field);
          optExts.push(...extList);
        });

        const mimeOk = !!optMime && !!fileMime && optMime === fileMime;
        const extOk = !!fileExt && optExts.some((ext) => ext === fileExt);
        const suffixOk =
          !!name && optExts.some((ext) => name.toLowerCase().endsWith(ext));

        return mimeOk || extOk || suffixOk;
      });

      if (match) {
        const valueForDropdown = match.keyId ?? match.value ?? match.key;
        setDocumentData((prev) => ({
          ...prev,
          fileType: valueForDropdown,
        }));
      } else {
        // Not strictly necessary, but helpful if your category truly lacks this type
        console.warn("[DocUpload] No matching extension option for file", {
          fileMime,
          fileExt,
          extensionOptions,
        });
      }
    };

    useEffect(() => {
      if (!editMode || !currentValues) return;

      let cancelled = false;

      const initialDocuments = currentValues?.map((currentValue, index) => ({
        ...currentValue,
        id: index + 2,
        file: null,
        isRowLoading: true,
        fileLoadError: false,
      }));

      setDocuments(initialDocuments);
      setDocCount(initialDocuments.length + 2);

      initialDocuments.forEach(async (doc) => {
        try {
          const fileData = await getFile(doc.docId);
          if (cancelled) return;

          const file = new File([fileData], doc.docName, {
            type: doc.fileTypeMime ?? fileData.type,
          });

          setDocuments((prev) =>
            prev.map((item) =>
              item.id === doc.id
                ? { ...item, file, isRowLoading: false }
                : item,
            ),
          );
        } catch (e) {
          if (cancelled) return;

          setDocuments((prev) =>
            prev.map((item) =>
              item.id === doc.id
                ? { ...item, isRowLoading: false, fileLoadError: true }
                : item,
            ),
          );
        }
      });

      return () => {
        cancelled = true;
      };
    }, [editMode, currentValues]);

    // useEffect(() => {
    //   (async () => {
    //     if (editMode) {
    //       setIsEditModeDataLoading(true);
    //       let currentDocuments = [];
    //       let id = 2;
    //       if (!currentValues) return;
    //       for (let currentValue of currentValues) {
    //         const fileData = await getFile(currentValue.docId);

    //         const file = new File([fileData], currentValue.docName, {
    //           type: currentValue.fileTypeMime ?? fileData.type,
    //         });

    //         currentDocuments.push({
    //           ...currentValue,
    //           file,
    //           id,
    //         });
    //         ++id;
    //       }
    //       setDocCount(id);
    //       setDocuments(currentDocuments);
    //       setIsEditModeDataLoading(false);
    //     }
    //   })();
    // }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setDocumentData({
          docOperation: opeType,
          docCategory: null,
          docReference: reference,
          fileType: null,
          fileName: null,
          docUploadStatus: 1,
          docName: "",
          docStatus: opeType === 8 ? 1 : 2,
          docActive: 1,
          docCategoryValue: "",
          fileTypeValue: "",
          fileNameValue: "",
          icon: "",
          author: user.username,
          persisted: false,
          isRowLoading: false,
          fileLoadError: false,
        });
        setCurrentErrors({});
        setFiles([]);
        setCurrentErrors({});
        setCurrentDocument({});
        setButtonClicked(false);
      }
    }, [buttonClicked, opeType, reference, user.username]);

    // Keep ONLY the keyId-based resolver
    useEffect(() => {
      (async () => {
        setIsExtensionLoading(true);
        const extensionData = await getDropdownData("file_type", "", [
          "keyValue2",
        ]);

        const extension = extensionData?.find(
          (ext) => ext.keyId === documentData.fileType,
        );

        setCurrentFileExtension(extension ? extension.keyValue2 : null);
        setIsExtensionLoading(false);
      })();
    }, [documentData.fileType]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            deleted: deletedList,
          };

          documents.forEach((doc) => {
            if (doc.docId && doc.docId !== null) {
              if (doc.modified) {
                updateState.modified.push({ ...doc, author: user.username });
              }
            } else {
              updateState.created.push({ ...doc, author: user.username });
            }
          });

          return updateState;
        } else {
          return documents;
        }
      },
      getAllDocuments: () => documents,

      resetForm: () => {
        setButtonClicked(true);
        setDocuments([]);
      },


      markUploadFailed: (docIdentifier) => {
        setDocuments((prev) =>
            prev.map((doc) =>
                doc.id === docIdentifier || doc.fileName === docIdentifier
                    ? { ...doc, fileLoadError: true }
                    : doc,
            ),
        );
      },
    }));

    function downloadBlob(fileOrBlob, suggestedName = "download") {
      const blob =
        fileOrBlob instanceof Blob
          ? fileOrBlob
          : new Blob([fileOrBlob], {
              type: fileOrBlob?.type || "application/octet-stream",
            });

      let filename =
        (fileOrBlob && fileOrBlob.name) || suggestedName || "download";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    const onCategoryChangeHandler = (e, data) => {
      const category = data.value;
      setDocumentData((prevState) => ({
        ...prevState,
        docCategory: category,
      }));
      setDocumentData((prevState) => ({
        ...prevState,
        fileType: null,
      }));

      setFiles([]);
      setDocCatSelected(true);
    };

    const typesToDownload = ["application/vnd.ms-outlook", "message/rfc822"];

    // const cellDoubleClickHandler = (e) => {
    //   if (e.colDef.field === "file") {
    //     const mimeType = e.data.fileTypeMime;
    //     const file = e.data.file;
    //     if (typesToDownload.includes(mimeType)) {
    //       const fallbackName = e.data.docName;
    //       downloadBlob(file, fallbackName);
    //       return;
    //     }

    //     const fileURL = URL.createObjectURL(file);

    //     window.open(fileURL, "_blank");
    //   }
    // };

    const cellDoubleClickHandler = (e) => {
      if (e.colDef.field === "file") {
        if (e.data.isRowLoading) {
          return;
        }

        if (e.data.fileLoadError || !e.data.file) {
          toast.warn("File is not available");
          return;
        }

        const mimeType = e.data.fileTypeMime;
        const file = e.data.file;

        if (typesToDownload.includes(mimeType)) {
          const fallbackName = e.data.docName;
          downloadBlob(file, fallbackName);
          return;
        }

        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      }
    };

    const onAddClick = async () => {
      setIsAddLoading(true);

      let docErrors = documentValidation({
        ...documentData,
      });

      const sameDocNameList = documents.filter(
        (doc) => doc.docName === documentData.docName,
      );

      if (files?.length === 0) {
        docErrors = {
          ...docErrors,
          file: "File must be selected",
        };
      }

      const nonUniqueRecord = hasMultiple({
        data: documents,
        newItem: documentData,
        validator: (item) => item.fileName === documentData.fileName,
        persistentIdentifier: "docId",
      });
      if (nonUniqueRecord) {
        if (!isOnTheModal) {
          toast.warn("Cannot add multiple records with same file name", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          toast.warn("Cannot add multiple records with same file name", {
            autoClose: 5000,
            containerId: "Modal",
          });
        }
        setIsAddLoading(false);
        return;
      }

      if (
        sameDocNameList.length >= 1 &&
        sameDocNameList[0].id !== documentData.id
      ) {
        docErrors = {
          ...docErrors,
          docName: "Can't have two documents with the same name",
        };
      }

      if (Object.keys(docErrors).length === 0) {
        const docUploadStatusValue = await getTextByValueAndKey(
          "doc_status",
          documentData.docUploadStatus,
        );
        const docCategoryValue = await getTextByValueAndKey(
          "doc_type",
          documentData.docCategory,
        );
        const fileTypeValue = await getByValueAndKey(
          "file_type",
          documentData.fileType,
        );
        const fileNameValue = await getTextByValueAndKey(
          "file_name",
          documentData.fileName,
        );

        if (!documentData.docId || documentData.docId === null) {
          if (!documentData.id || documentData.id === null) {
            setDocuments((prevState) => [
              ...prevState,
              {
                ...documentData,
                id: docCount,
                docCategoryValue,
                fileTypeValue: fileTypeValue?.keyValue1,
                icon: fileTypeValue?.keyValue3,
                fileNameValue,
                docUploadStatusValue,
                file: files[0],
              },
            ]);
            setDocCount((prevState) => ++prevState);
          } else {
            setDocuments((prevState) =>
              prevState.filter((item) => item.id !== documentData.id),
            );
            setDocuments((prevState) => [
              ...prevState,
              {
                ...documentData,
                docCategoryValue,
                fileTypeValue: fileTypeValue?.keyValue1,
                icon: fileTypeValue?.keyValue3,
                fileNameValue,
                docUploadStatusValue,
                file: files[0],
              },
            ]);
          }
        } else {
          setDocuments((prevState) =>
            prevState.filter((item) => item.docId !== documentData.docId),
          );
          setDocuments((prevState) => [
            ...prevState,
            {
              ...documentData,
              docCategoryValue,
              fileTypeValue: fileTypeValue?.keyValue1,
              icon: fileTypeValue?.keyValue3,
              fileNameValue,
              docUploadStatusValue,
              file: files[0],
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }
      setCurrentErrors(docErrors);
      setIsAddLoading(false);
    };

    useEffect(() => {
      if (files?.length > 0) {
        setCurrentErrors((prev) => ({
          ...prev,
          file: "",
        }));
      }
    }, [files]);

    // Populate form when Edit is clicked
    useEffect(() => {
      if (currentDocument && Object.keys(currentDocument).length > 0) {
        setDocumentData({
          ...currentDocument,
          author: user.username,
        });

        // Set the file for the uploader
        if (currentDocument.file) {
          setFiles([currentDocument.file]);
        }
      }
    }, [currentDocument, user.username]);

    function getBaseName(filename) {
      const idx = filename.lastIndexOf(".");
      return idx < 0 ? filename : filename.substring(0, idx);
    }

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <form
              className="ui form"
              style={
                showGridOnly
                  ? { overflowY: "auto", height: "50vh" }
                  : { overflowY: "auto", height: "75vh" }
              }
            >
              <div className="ui segment">
                {!showGridOnly && (
                  <>
                    <h4 className="ui dividing header">Document Upload</h4>
                    <div className="two fields">
                      <Dropdown
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_category"
                        editMode={editMode}
                        label="Document Category"
                        required={true}
                        error={currentErrors.docCategory}
                        disabled={
                          viewOnlyMode || (editMode && currentDocument?.id)
                        }
                        placeholder="Document Type"
                        options={dropdown.documentTypeOptions}
                        value={documentData.docCategory}
                        style={{ height: "5px" }}
                        onChange={onCategoryChangeHandler}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { docCategory: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />

                      <Dropdown
                        label="Extensions"
                        required={true}
                        error={currentErrors.fileType}
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_file_type"
                        editMode={editMode}
                        placeholder={
                          isExtOptLoading ? "Loading..." : "Extensions"
                        }
                        options={extensionOptions}
                        value={documentData.fileType}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setDocumentData((prevState) => ({
                            ...prevState,
                            fileType: data.value,
                          }));
                          setFiles([]);
                        }}
                        disabled={true}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { fileType: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />
                    </div>

                    <div className="two fields">
                      <Dropdown
                        label="File Name"
                        required={true}
                        disabled={viewOnlyMode}
                        error={currentErrors.fileName}
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_file_name"
                        editMode={editMode}
                        placeholder="File Name"
                        options={dropdown.fileNameOptions}
                        value={documentData.fileName}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { fileName: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        onChange={(e, data) => {
                          setDocumentData((prevState) => ({
                            ...prevState,
                            fileName: data.value,
                          }));
                        }}
                      />

                      <Input
                        name="documentName"
                        readOnly={viewOnlyMode}
                        placeholder="Document Name"
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_name"
                        editMode={editMode}
                        value={documentData.docName}
                        required={true}
                        label="Document Name"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { docName: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        error={currentErrors.docName}
                        onChange={(e) => {
                          const value = e.target.value.trim();
                          const newName = value.toLowerCase();
                          const sameNameDoc = documents.find(
                            (doc) =>
                              getBaseName(doc.docName).toLowerCase() ===
                                newName ||
                              doc.docName.toLowerCase() === newName,
                          );

                          if (sameNameDoc) {
                            if (
                              sameNameDoc.id !== documentData.id ||
                              sameNameDoc.docId !== documentData.docId
                            ) {
                              setCurrentErrors((prevState) => ({
                                ...prevState,
                                docName:
                                  "Can't have two documents with the same name",
                              }));
                            } else {
                              setCurrentErrors((prevState) => {
                                const { docName: _, ...rest } = prevState;
                                return rest;
                              });
                            }
                          } else {
                            setCurrentErrors((prevState) => {
                              const { docName: _, ...rest } = prevState;
                              return rest;
                            });
                          }
                          setDocumentData((prevState) => ({
                            ...prevState,
                            docName: value,
                          }));
                        }}
                      />
                    </div>

                    <div className="two fields">
                      <Dropdown
                        label="Status"
                        required={true}
                        placeholder="Status"
                        error={currentErrors.docStatus}
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_status"
                        editMode={editMode}
                        disabled={
                          viewOnlyMode ||
                          (opeType === 8 && documentData.persisted === false)
                        }
                        options={dropdown.statusOptions}
                        value={documentData.docStatus}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setDocumentData((prevState) => ({
                            ...prevState,
                            docStatus: data.value,
                          }));
                        }}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { docStatus: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />
                      {/* <Dropdown
                        label="Active"
                        required={true}
                        placeholder="Active"
                        error={currentErrors.docActive}
                        module={module}
                        form={`${FORM}_${opeType}`}
                        field="doc_active"
                        authenticated={true}
                        needDeletePermission={true}
                        editMode={editMode}
                        disabled={viewOnlyMode}
                        options={dropdown.activeOptions}
                        value={documentData.docActive}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setDocumentData((prevState) => ({
                            ...prevState,
                            docActive: data.value,
                          }));
                        }}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { docActive: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      /> */}
                    </div>

                    <div
                      className={`field ${currentErrors.file ? "error" : ""}`}
                    >
                      <label>
                        <label
                          style={{
                            color: "#9f3a38",
                            fontSize: "20px",
                            marginRight: "2px",
                          }}
                        >
                          *
                        </label>
                        Upload File
                      </label>
                      <FileUploader
                        files={files}
                        setFiles={setFiles}
                        maxFiles={1}
                        errors={currentErrors}
                        disabled={viewOnlyMode || isExtensionLoading}
                        documentData={documentData}
                        isOnTheModal={isOnTheModal}
                        onFileSelected={handleFileSelected}
                        extensionOptions={extensionOptions}
                      />
                      <small style={{ color: "#9f3a38" }}>{`${
                        currentErrors.file ? currentErrors.file : ""
                      }`}</small>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        template="reset"
                        onClick={() => {
                          setButtonClicked(true);
                        }}
                      />
                      <Button
                        template="addOrSave"
                        disabled={viewOnlyMode}
                        authenticated={true}
                        editMode={editMode}
                        module={module}
                        form={`${FORM}_${opeType}`}
                        create={Object.keys(currentDocument).length === 0}
                        onClick={onAddClick}
                        isLoading={isAddLoading}
                      />
                    </div>
                  </>
                )}

                <Grid
                  heading="Document List"
                  isLoading={isEditModeDataLoading}
                  onCellDoubleClicked={cellDoubleClickHandler}
                  config={[
                    {
                      headerName: "Doc Type",
                      field: "docCategoryValue",
                      cellStyle: (params) =>
                          params.data.fileLoadError
                              ? { backgroundColor: "#fff0f0", color: "#9f3a38" }
                              : null,
                    },
                    {
                      headerName: "Document Name",
                      field: "docName",
                      cellStyle: (params) =>
                          params.data.fileLoadError
                              ? { backgroundColor: "#fff0f0", color: "#9f3a38" }
                              : null,
                    },
                    {
                      headerName: "Extension",
                      field: "fileTypeValue",
                      cellStyle: (params) =>
                          params.data.fileLoadError
                              ? { backgroundColor: "#fff0f0", color: "#9f3a38" }
                              : null,
                    },
                    {
                      headerName: "File Name",
                      field: "fileNameValue",
                      cellStyle: (params) =>
                          params.data.fileLoadError
                              ? { backgroundColor: "#fff0f0", color: "#9f3a38" }
                              : null,
                    },
                    // {
                    //   headerName: "File",
                    //   field: "file",
                    //   cellRenderer: FileCellRenderer,
                    //   cellRendererParams: (params) => ({
                    //     file: params.data.file,
                    //     icon: params.data.icon,
                    //   }),
                    // },
                    {
                      headerName: "File",
                      field: "file",
                      cellRenderer: FileCellRenderer,
                      cellRendererParams: (params) => ({
                        file: params.data.file,
                        icon: params.data.icon,
                        isRowLoading: params.data.isRowLoading,
                        fileLoadError: params.data.fileLoadError,
                      }),
                    },
                    {
                      headerName: "Upload Status",
                      field: "docUploadStatusValue",
                      cellStyle: (params) =>
                          params.data.fileLoadError
                              ? { backgroundColor: "#fff0f0", color: "#9f3a38" }
                              : null,
                    },
                    // {
                    //   headerName: "Load Status",
                    //   field: "isRowLoading",
                    //   valueGetter: (params) => {
                    //     if (params.data.isRowLoading) return "Loading";
                    //     if (params.data.fileLoadError) return "Failed";
                    //     return "Ready";
                    //   },
                    // },
                    {
                      field: "Edit",
                      filter: true,
                      cellRenderer: ButtonRenderer,
                      cellRendererParams: (params) => ({
                        clickHandler: () => {
                          setCurrentDocument(params.data);
                        },
                        label: viewOnlyMode ? "View" : "Edit",
                        authenticated: true,
                        form: `${FORM}_${opeType}`,
                        module: module,
                        editMode: editMode,
                        consider: AUTH_TYPES.CREATE,
                        disabled:
                          params.data.nonEditable === true ||
                          params.data.isRowLoading === true,
                      }),
                    },
                    {
                      field: "Delete",
                      filter: true,
                      cellRenderer: DeleteButtonRenderer,
                      cellRendererParams: (params) => ({
                        consider: AUTH_TYPES.DELETE,
                        clickHandler: async () => {
                          if (params.data.docId) {
                            setDeletedList((prev) => [
                              ...prev,
                              params.data.docId,
                            ]);
                          }
                          setDocuments((prevDocuments) =>
                            prevDocuments.filter(
                              (document) => document.id !== params.data.id,
                            ),
                          );
                        },
                        message:
                          "Are you sure you want to delete this Document?",
                        label: "Delete",
                        className: "ui red button",
                        disabled:
                          viewOnlyMode || params.data.isRowLoading === true,
                        authenticated: true,
                        form: `${FORM}_${opeType}`,
                        module: module,
                        editMode:
                          params.data.docId !== null &&
                          isNumber(params.data.docId),
                      }),
                    },
                  ]}
                  content={documents}
                />
              </div>
            </form>
          </>
        )}
      </>
    );
  },
);

export default DocumentUpload;
